import {isFeatureEnabled} from '@github-ui/feature-flags'
import {MAX_QUERIES_TO_MARK_FOR_PREHEATING} from '@github-ui/relay-query-cache/contexts/RelayCachePreheatContext'
import {ssrSafeLocation} from '@github-ui/ssr-utils'
import {getHighlightedEvent} from '@github-ui/timeline-items/HighlightedEvent'
import {LABELS} from '@github-ui/timeline-items/Labels'
import {VALUES} from '@github-ui/timeline-items/Values'
import {Timeline} from '@primer/react'
import {useEffect, useMemo, useRef, useState} from 'react'

import {TEST_IDS} from '../../constants/test-ids'
import {useCachePreheatIssueViewerQueries} from '../../hooks/use-cache-preheat-issueviewer'
import {useSafariTimelineScrollObserver} from '../../hooks/use-safari-scroll-preservation'
import {useNewScrollToHighlighted} from '../../hooks/use-scroll-to-highlighted'
import {gatherIssueLinks} from '../../utils/helpers'
import {parseGitHubIssueUrl} from '../../utils/urls'
import {useIssueViewerSubscription} from '../IssueViewerSubscription'
import {CrawlerPagination} from './CrawlerPagination'
import type {TimelineItem} from './IssueTimeline'
import type {EventProps} from './IssueTimelineItem'
import {useTimelineConnectionIds} from './TimelineConnectionContext'
import {TimelineTransferringFlash} from './TimelineTransferringFlash'
import {isEventHighlighted} from './use-timeline-highlight'
import {VirtualIssueTimelinePage} from './VirtualIssueTimelinePage'

type IssueTimelineLiveUpdatesLoaderProps = {
  issueId: string
  itemCount: number
}
const IssueTimelineLiveUpdatesLoader = ({issueId, itemCount}: IssueTimelineLiveUpdatesLoaderProps) => {
  const {back} = useTimelineConnectionIds()
  useIssueViewerSubscription(issueId, itemCount, back)
  return null
}

type TimelinePanelProps = {
  timelineItems: TimelineItem[] | null
  totalItemCount: number
  highlightedEvent: string | undefined
  highlightedReady: boolean
  issueId: string
  repositoryId: string
  repositoryNameWithOwner: string
  issueUrl: string
  page?: number
  isTransferInProgress?: boolean
  showCopilotMenuItem?: boolean
} & EventProps

/**
 * Shared rendering component for timeline items. Handles:
 * - Highlight state (parse, click-to-dismiss, restore on change)
 * - Scroll-to-highlighted + Safari scroll preservation
 * - Paginating timeline items into virtual pages
 * - Link preheating for referenced issues
 * - Crawler pagination for bots
 * - Rendering `<Timeline>` with `<VirtualIssueTimelinePage>` children
 *
 * Used by both ClassicTimeline and TabbedTimeline.
 */
export function TimelinePanel({
  timelineItems,
  totalItemCount,
  highlightedEvent,
  highlightedReady,
  issueId,
  repositoryId,
  repositoryNameWithOwner,
  issueUrl,
  page,
  isTransferInProgress,
  showCopilotMenuItem,
  viewer,
  onCommentChange,
  onCommentReply,
  onCommentEditCancel,
  optionConfig,
}: TimelinePanelProps) {
  const [issueLinks, setIssueLinks] = useState<string[]>([])

  const highlight = useMemo(() => getHighlightedEvent(highlightedEvent), [highlightedEvent])

  // This state controls if the highlight borders should be shown or not
  const [shouldHighlightElement, setShouldHighlightElement] = useState<boolean>(highlight !== undefined)

  // After rendering, clicking anywhere should remove the borders
  useEffect(() => {
    const handlePageClick = () => {
      setShouldHighlightElement(false)
    }
    document.addEventListener('click', handlePageClick)

    return () => {
      document.removeEventListener('click', handlePageClick)
    }
  }, [highlight])

  // If a new element's link is clicked, we should restore the highlight
  useEffect(() => {
    if (!highlight?.id) return

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setShouldHighlightElement(true)
  }, [highlight?.id])

  const timelinePaginationForBotsEnabled = isFeatureEnabled('issues_react_bots_timeline_pagination')
  const hasNextPage = timelinePaginationForBotsEnabled && getHasNextPage(totalItemCount, page)

  // Ref for the timeline container to gather issue links on demand
  const timelineContainerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const links = gatherIssueLinks(timelineContainerRef.current)
    setIssueLinks(links)
  }, [totalItemCount])

  const issuesWithLinks: Array<{owner: string; repo: string; number: number}> = useMemo(() => {
    const items: Array<{owner: string; repo: string; number: number}> = []
    for (const link of issueLinks) {
      const parsed = parseGitHubIssueUrl(link)
      if (parsed) {
        items.push(parsed)
        if (items.length >= MAX_QUERIES_TO_MARK_FOR_PREHEATING) {
          break
        }
      }
    }
    return items
  }, [issueLinks])

  useCachePreheatIssueViewerQueries({
    issues: issuesWithLinks,
    source: 'IssueTimeline',
  })

  // Iterate items to check if the loaded highlight is in the list
  // Room for performance optimization if we face issues in large timelines, as we can determine
  // if the highlighted item is already present during the mapping process.
  // https://github.com/github/github/blob/9ecd32f60f96a519f99462c48f6235d5842ca814/ui/packages/issue-viewer/components/timeline/IssueTimeline.tsx#L249
  const highlightedItemRef = useRef<HTMLDivElement>(null)
  const timelineRef = useRef<HTMLDivElement>(null)

  const isHighlightLoaded = useMemo(() => {
    if (!timelineItems) return false
    if (!shouldHighlightElement || !highlight) return false

    return timelineItems.some(item => {
      if (item.type !== 'event' || !item.item) return false

      return isEventHighlighted(item.item, highlight)
    })
  }, [highlight, timelineItems, shouldHighlightElement])

  useNewScrollToHighlighted(isHighlightLoaded, highlightedItemRef, highlightedEvent, highlightedReady)

  // Safari doesn't support CSS scroll anchoring, so we need to manually observe
  // DOM changes and preserve scroll position when timeline items are added/removed
  useSafariTimelineScrollObserver(timelineRef)

  if (!timelineItems) {
    throw new Error(
      `Missing timeline items for issue ${issueId}. Total item count: ${totalItemCount}. Highlighted event: ${highlightedEvent}`,
    )
  }

  const paginatedTimelineItems = useMemo(() => {
    const pageSize = VALUES.timeline.virtualPageSize
    const pages = []
    if (timelineItems.length === 0) {
      return []
    }
    const getTimelineItemCount = (item: TimelineItem) => {
      // undefined items or load items are not counted
      if (!item || item.type !== 'event') {
        return 0 // Load items count as 1
      }
      if (!item.rollupGroup) {
        return 1 // Single event item
      }
      // If the item has a rollup group, count the number of rolled up events for each rollup type
      return Object.values(item.rollupGroup).reduce((count, rollup) => count + rollup.length, 0)
    }

    let currentPage: TimelineItem[] = []
    let currentPageItemCount = 0

    for (const timelineItem of timelineItems) {
      const itemCount = getTimelineItemCount(timelineItem)

      // Check if adding this item would exceed the page size
      if (currentPageItemCount + itemCount > pageSize && currentPage.length > 0) {
        // Add current page to pages and start a new page
        pages.push(currentPage)
        currentPage = [timelineItem]
        currentPageItemCount = itemCount
      } else {
        // Add item to current page
        currentPage.push(timelineItem)
        currentPageItemCount += itemCount
      }
    }

    // Add the last page if it has items
    if (currentPage.length > 0) {
      pages.push(currentPage)
    }

    return pages
  }, [timelineItems])

  return (
    <div ref={timelineContainerRef}>
      {Boolean(viewer) && <IssueTimelineLiveUpdatesLoader issueId={issueId} itemCount={totalItemCount} />}
      {isTransferInProgress && <TimelineTransferringFlash />}
      <h2 className="sr-only">{LABELS.timeline.header}</h2>
      {hasNextPage && <CrawlerPagination nextPage={page ? page + 1 : 1} canonicalUrl={ssrSafeLocation.pathname} />}
      <Timeline ref={timelineRef} id="issue-timeline" data-testid={TEST_IDS.issueTimelineContainer}>
        {paginatedTimelineItems.map(pageItems => (
          <VirtualIssueTimelinePage
            key={`page-${pageItems.length}-${
              pageItems[0]?.type === 'event' ? `event-${pageItems[0]?.item?.__id}` : `load`
            }`}
            timelineItems={pageItems}
            shouldHighlightElement={shouldHighlightElement}
            highlight={highlight}
            viewer={viewer}
            issueId={issueId}
            repositoryId={repositoryId}
            repositoryNameWithOwner={repositoryNameWithOwner}
            issueUrl={issueUrl}
            onCommentChange={onCommentChange}
            onCommentReply={onCommentReply}
            onCommentEditCancel={onCommentEditCancel}
            optionConfig={optionConfig}
            highlightedItemRef={highlightedItemRef}
            showCopilotMenuItem={showCopilotMenuItem}
          />
        ))}
      </Timeline>
    </div>
  )
}

function getHasNextPage(totalItemCount: number, page?: number): boolean {
  if (!page) {
    // The initial load returns 30 items (15 from front and 15 from back)
    return totalItemCount > 30
  }

  // 150 is the page size (same size we use when a regular user clicks "Load more")
  return totalItemCount > 15 + page * 150
}
